import React, { useEffect, useState } from 'react';
import api from '../utils/api';

interface SystemConfig {
  emailTestMode: boolean;
  testEmails: string[];
  bccEmail: string;
  stripeBypassEmails: string[];
}

export default function AdminSettings() {
  const [config, setConfig] = useState<SystemConfig>({ emailTestMode: false, testEmails: [], bccEmail: '', stripeBypassEmails: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const [bccInput, setBccInput] = useState('');
  const [testModeEnabled, setTestModeEnabled] = useState(false);
  const [testEmailsInput, setTestEmailsInput] = useState('');
  const [stripeBypassEmails, setStripeBypassEmails] = useState<string[]>([]);
  const [bypassAddInput, setBypassAddInput] = useState('');

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    api.get('/api/system-config/email-mode')
      .then(res => {
        setConfig(res.data);
        setBccInput(res.data.bccEmail || '');
        setTestModeEnabled(res.data.emailTestMode);
        setTestEmailsInput((res.data.testEmails || []).join(', '));
        setStripeBypassEmails(res.data.stripeBypassEmails || []);
      })
      .catch(() => showToast('error', 'Failed to load settings.'))
      .finally(() => setLoading(false));
  }, []);

  const addBypassEmail = () => {
    const email = bypassAddInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast('error', 'Enter a valid email address.');
      return;
    }
    if (stripeBypassEmails.includes(email)) {
      showToast('error', 'That email is already on the list.');
      return;
    }
    setStripeBypassEmails(prev => [...prev, email]);
    setBypassAddInput('');
  };

  const removeBypassEmail = (email: string) => {
    setStripeBypassEmails(prev => prev.filter(e => e !== email));
  };

  const save = async () => {
    const trimmed = bccInput.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      showToast('error', 'BCC email address is not valid.');
      return;
    }

    const testEmails = testEmailsInput
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      const res = await api.post('/api/system-config/email-mode', {
        enabled: testModeEnabled,
        testEmails,
        bccEmail: trimmed,
        stripeBypassEmails,
      });
      setConfig(res.data);
      showToast('success', 'Settings saved.');
    } catch {
      showToast('error', 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '2rem', fontFamily: 'IBM Plex Sans, system-ui, sans-serif', maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0F1F3D', margin: 0 }}>
          Admin Settings
        </h1>
        <p style={{ color: '#6b7280', marginTop: 4 }}>
          Configure email delivery, test mode, and billing bypass for internal test accounts.
        </p>
      </div>

      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* BCC card */}
          <div style={cardStyle}>
            <h2 style={cardHeading}>Support BCC</h2>
            <p style={cardDesc}>
              All transactional emails sent to users (deadline reminders, onboarding, team invites, contact confirmations)
              will be blind-copied to this address. Leave blank to disable.
              Auth emails (OTP codes, password resets) are never BCC'd for security.
            </p>
            <label style={labelStyle}>BCC email address</label>
            <input
              type="email"
              value={bccInput}
              onChange={e => setBccInput(e.target.value)}
              placeholder="support@cectrack.com"
              style={inputStyle}
            />
          </div>

          {/* Test mode card */}
          <div style={cardStyle}>
            <h2 style={cardHeading}>Email Test Mode</h2>
            <p style={cardDesc}>
              When enabled, outgoing emails are only delivered to addresses on the allowlist below.
              All other recipients are silently skipped. Use this to preview campaigns before going live.
            </p>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <div
                onClick={() => setTestModeEnabled(v => !v)}
                style={{
                  width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
                  background: testModeEnabled ? '#0F1F3D' : '#d1d5db',
                  position: 'relative', flexShrink: 0,
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: testModeEnabled ? 23 : 3,
                  transition: 'left 0.2s',
                }} />
              </div>
              <span style={{ fontWeight: testModeEnabled ? 600 : 400, color: testModeEnabled ? '#0F1F3D' : '#6b7280' }}>
                {testModeEnabled ? 'Test mode ON — only allowlisted addresses receive emails' : 'Test mode OFF — all emails deliver normally'}
              </span>
            </label>

            {testModeEnabled && (
              <div style={{ marginTop: '1rem' }}>
                <label style={labelStyle}>Allowed email addresses (comma-separated)</label>
                <textarea
                  value={testEmailsInput}
                  onChange={e => setTestEmailsInput(e.target.value)}
                  placeholder="you@example.com, qa@cectrack.com"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
                {testEmailsInput.trim() && (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {testEmailsInput.split(',').map(e => e.trim()).filter(Boolean).map(email => (
                      <span key={email} style={{
                        background: '#e0f2fe', color: '#0369a1', fontSize: '0.75rem',
                        padding: '2px 8px', borderRadius: 4, fontWeight: 500,
                      }}>{email}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stripe bypass card */}
          <div style={cardStyle}>
            <h2 style={cardHeading}>Test Accounts — Stripe Bypass</h2>
            <p style={cardDesc}>
              Emails listed here can activate any plan (Free, Pro, Starter, Firm, Practice) directly
              without going through Stripe checkout. Use for internal QA accounts only.
              When a bypassed user clicks a plan upgrade, their account is activated immediately
              with a 1-year period end — no payment required.
            </p>

            {/* Add email */}
            <label style={labelStyle}>Add account email</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={bypassAddInput}
                onChange={e => setBypassAddInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBypassEmail(); } }}
                placeholder="test@cectrack.com"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={addBypassEmail}
                style={{
                  padding: '0.5rem 1rem', borderRadius: 7, border: 'none',
                  background: '#0F1F3D', color: '#fff', fontWeight: 600,
                  fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Add
              </button>
            </div>

            {/* Current list */}
            {stripeBypassEmails.length > 0 ? (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  Bypass list ({stripeBypassEmails.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {stripeBypassEmails.map(email => (
                    <div key={email} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: '#fefce8', border: '1px solid #fde68a', borderRadius: 7,
                      padding: '0.4rem 0.75rem',
                    }}>
                      <span style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: 500 }}>{email}</span>
                      <button
                        onClick={() => removeBypassEmail(email)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#b45309', fontSize: '1rem', lineHeight: 1, padding: '0 2px',
                          fontWeight: 700,
                        }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
                No bypass accounts configured.
              </p>
            )}
          </div>

          {/* Save */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={save}
              disabled={saving}
              style={{
                padding: '0.55rem 1.5rem', borderRadius: 7, border: 'none',
                background: saving ? '#94a3b8' : '#0F1F3D', color: '#fff',
                fontSize: '0.9rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>

        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 100,
          padding: '0.75rem 1.25rem', borderRadius: 8, fontWeight: 500, fontSize: '0.875rem',
          background: toast.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: toast.type === 'success' ? '#15803d' : '#b91c1c',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '1.5rem',
};
const cardHeading: React.CSSProperties = {
  fontSize: '1rem', fontWeight: 700, color: '#0F1F3D', margin: '0 0 0.5rem',
};
const cardDesc: React.CSSProperties = {
  fontSize: '0.875rem', color: '#6b7280', margin: '0 0 1rem', lineHeight: 1.6,
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151',
  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem 0.75rem', borderRadius: 7,
  border: '1px solid #d1d5db', fontSize: '0.9rem', boxSizing: 'border-box',
  outline: 'none',
};
